import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IPlan, SubscriptionType } from "~/types/billing";
import { usePlans } from "~/api/billing/queries";
import PlanCard from "./planCard";
import Loader from "~/components/ui/Loader";
import { useDebounceEffect } from "~/hooks/useDebounceEffect";
import * as S from "./plans.style";
import { useAccount } from "~/hooks/useAccount";
import { TogglePlan } from "./togglePlan";
import { SVG } from "~/components/ui/SVG";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { getMaxMonthsOff } from "~/helpers/plans";

type Props = {
  closeModal?: () => void;
  initialIsAnnual?: boolean;
  handleClickCheckout?: (plan: IPlan) => void;
};

export const Plans = ({
  closeModal,
  initialIsAnnual = false,
  handleClickCheckout = undefined,
}: Props) => {
  const { t } = useTranslation();
  const { data: plans, isLoading } = usePlans();
  const { account, getAccount } = useAccount();
  const [isAnnual, setIsAnnual] = useState<boolean>(initialIsAnnual);
  const isCurrentPlanAnnual =
    account?.active_subscription?.plan?.interval === SubscriptionType.ANNUAL;

  const activePlans = useMemo(() => {
    return plans?.filter((plan) => plan.status === "AC");
  }, [plans]);

  const filteredPlans = useMemo(() => {
    if (isAnnual)
      return activePlans?.filter(
        (plan) => plan.interval === SubscriptionType.ANNUAL
      );
    return activePlans?.filter(
      (plan) => plan.interval === SubscriptionType.MONTHLY && plan.for_winning
    );
  }, [activePlans, isAnnual]);

  const biggestMonthsOff = useMemo(() => getMaxMonthsOff(plans), [plans]);
  const isInShopifyAppReview = useMemo(
    () => account?.is_annual_shopify || false,
    [account]
  );
  const toggleControl = useCallback(
    () =>
      isCurrentPlanAnnual && !isInShopifyAppReview
        ? setIsAnnual(true)
        : setIsAnnual(!isAnnual),
    [isCurrentPlanAnnual, isAnnual]
  );

  const highestPricePlan = useMemo(
    () =>
      filteredPlans?.reduce(
        (prev, current) =>
          current.cost_per_month > prev.cost_per_month ? current : prev,
        filteredPlans[0]
      ),
    [filteredPlans]
  );

  useEffect(() => {
    if (isCurrentPlanAnnual) setIsAnnual(true);
  }, [isCurrentPlanAnnual]);

  useDebounceEffect(
    () => {
      /* Fix the current plan bug when a user subscribes, and the store is not updated with the required information
    about then plan.
    */
      getAccount();
    },
    [],
    100
  );

  if (isLoading) return <Loader />;

  return (
    <Fragment>
      <S.PageTitle>{t("settings.choose_plan")}</S.PageTitle>

      <TogglePlan
        isAnnual={isAnnual}
        toggleControl={toggleControl}
        biggestMonthsOff={biggestMonthsOff}
      />

      {isCurrentPlanAnnual ? (
        <S.DisclaimerBanner>
          <SVG icon={faInfoCircle} />
          <S.DisclaimerText>
            Note: You cannot downgrade from Yearly to Monthly plans.
          </S.DisclaimerText>
        </S.DisclaimerBanner>
      ) : null}

      <S.PlansGrid gap={closeModal === undefined ? 1 : 2}>
        {filteredPlans?.map((plan, index) => {
          let previousPlanName =
            index > 0 ? filteredPlans[index - 1].name : null;
          if (isAnnual && index === 0) {
            previousPlanName =
              activePlans?.filter(
                (plan) => plan.interval === SubscriptionType.MONTHLY
              )[0].name || null;
          }

          return (
            <PlanCard
              account={account}
              key={plan.id}
              plan={plan}
              closeModal={closeModal}
              isHighestPrice={plan.id === highestPricePlan?.id}
              previousPlanName={previousPlanName}
              handleClick={handleClickCheckout}
            />
          );
        })}
      </S.PlansGrid>
    </Fragment>
  );
};
